import { rand, randFloat, randNumber, randSentence } from '@ngneat/falso';
import {
  calculateTaxFromTotal,
  TaxRate,
  TaxStrategy,
} from '@principle-theorem/accounting';
import {
  IAccountCredit,
  IAppointment,
  ICreditRefundLineItem,
  ICreditTransferLineItem,
  IDepositLineItem,
  IFeeLineItem,
  IInvoice,
  IInvoiceLineItem,
  InvoiceLineItemType,
  IPatient,
  IProductLineItem,
  IRefundLineItem,
  IServiceCodeLineItem,
  IStaffer,
  ITreatmentBasePriceLineItem,
  ITreatmentCategory,
  ITreatmentLineItem,
  ITreatmentPlan,
  RefundType,
  ServiceCodeType,
} from '@principle-theorem/principle-core/interfaces';
import {
  BaseFirestoreMock,
  getEnumValues,
  Properties,
} from '@principle-theorem/shared';
import { MockDocRef, MockNamedDocument } from '@principle-theorem/testing';
import { v4 as uuid } from 'uuid';

export class InvoiceLineItemMock<
    Interface extends IInvoiceLineItem = Properties<IInvoiceLineItem>,
  >
  extends BaseFirestoreMock<Interface>
  implements Properties<IInvoiceLineItem>
{
  uid = uuid();
  description = randSentence();
  amount = randFloat({ min: 1, max: 1000 });
  tax = calculateTaxFromTotal(this.amount, TaxRate.GST);
  taxStatus = rand(getEnumValues(TaxStrategy));
  quantity = randNumber({ min: 1, max: 3 });
}

export class TreatmentLineItemMock
  extends InvoiceLineItemMock<Properties<ITreatmentLineItem>>
  implements Properties<ITreatmentLineItem>
{
  type = InvoiceLineItemType.Treatment;
  items = [new ServiceLineItemMock(), new TreatmentBasePriceLineItemMock()];
  treatmentRef = {
    planRef: MockDocRef<ITreatmentPlan>(),
    treatmentUuid: uuid(),
    attributedTo: MockNamedDocument<IStaffer>(),
  };
  patientRef = MockDocRef<IPatient>();
}

export class ServiceLineItemMock
  extends InvoiceLineItemMock<Properties<IServiceCodeLineItem>>
  implements Properties<IServiceCodeLineItem>
{
  toothId = '12';
  type = InvoiceLineItemType.ServiceCode;
  code = '071';
  serviceCodeType = ServiceCodeType.ADA;
  extendedData = {};
}

export class DepositLineItemMock
  extends InvoiceLineItemMock<Properties<IDepositLineItem>>
  implements Properties<IDepositLineItem>
{
  type = InvoiceLineItemType.Deposit;
  max = randFloat({ min: 20, max: 200 });
  treatments = [MockNamedDocument()];
  attributedTo = MockNamedDocument<IStaffer>();
  forTreatmentCategoryRef = MockDocRef<ITreatmentCategory>();
  appointmentRef = MockDocRef<IAppointment>();
}

export class RefundLineItemMock
  extends InvoiceLineItemMock<Properties<IRefundLineItem>>
  implements Properties<IRefundLineItem>
{
  type = InvoiceLineItemType.Refund;
  refundType = RefundType.Card;
  sourceInvoice = MockDocRef<IInvoice>();
}

export class TreatmentBasePriceLineItemMock
  extends InvoiceLineItemMock<Properties<ITreatmentBasePriceLineItem>>
  implements Properties<ITreatmentBasePriceLineItem>
{
  type = InvoiceLineItemType.TreatmentBasePrice;
  treatmentRef = {
    planRef: MockDocRef<ITreatmentPlan>(),
    treatmentUuid: uuid(),
    attributedTo: MockNamedDocument<IStaffer>(),
  };
}

export class ProductLineItemMock
  extends InvoiceLineItemMock<Properties<IProductLineItem>>
  implements Properties<IProductLineItem>
{
  type = InvoiceLineItemType.Product;
  attributedTo = MockNamedDocument<IStaffer>();
}

export class FeeLineItemMock
  extends InvoiceLineItemMock<Properties<IFeeLineItem>>
  implements Properties<IFeeLineItem>
{
  type = InvoiceLineItemType.Fee;
  attributedTo = MockNamedDocument<IStaffer>();
}

export class CreditRefundLineItemMock
  extends InvoiceLineItemMock<Properties<ICreditRefundLineItem>>
  implements Properties<ICreditRefundLineItem>
{
  type = InvoiceLineItemType.CreditRefund;
  creditsUsed = [
    {
      accountCreditRef: MockDocRef<IAccountCredit>(),
      amount: randFloat({ min: 1, max: 1000 }),
    },
  ];
}

export class CreditTransferLineItemMock
  extends InvoiceLineItemMock<Properties<ICreditTransferLineItem>>
  implements Properties<ICreditTransferLineItem>
{
  type = InvoiceLineItemType.CreditTransfer;
  patientTransferredRefs = {
    from: MockDocRef<IPatient>(),
    to: MockDocRef<IPatient>(),
  };
  attributedTo = MockNamedDocument<IStaffer>();
}
